import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import { tokenManager } from '../utils/tokenManager';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = tokenManager.getAccessToken();
    const storedRefreshToken = tokenManager.getRefreshToken();
    const storedUser = tokenManager.getUser();

    if (storedToken && storedUser) {
      setToken(storedToken);
      setRefreshToken(storedRefreshToken);
      setUser(storedUser);
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (identifier, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.login({ identifier, password });

      if (response.success) {
        // Backend returns { success:true, message, data: { accessToken, refreshToken, user } }
        const { data } = response;
        const accessToken = data?.accessToken || data?.token; // Support both old and new format
        const newRefreshToken = data?.refreshToken;
        const userData = data?.user || data;

        setToken(accessToken);
        setRefreshToken(newRefreshToken);
        setUser(userData);

        // Store in localStorage using tokenManager
        tokenManager.setTokens(accessToken, newRefreshToken, userData);

        return { success: true, data: userData };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.register(userData);

      if (response.success) {
        const { data } = response;
        const accessToken = data?.accessToken || data?.token;
        const newRefreshToken = data?.refreshToken;
        const userData = data?.user || data;

        setToken(accessToken);
        setRefreshToken(newRefreshToken);
        setUser(userData);

        // Store in localStorage using tokenManager
        tokenManager.setTokens(accessToken, newRefreshToken, userData);

        return { success: true, data: userData };
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (err) {
      const errorMessage = err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setError(null);
    
    // Clear tokens using tokenManager
    tokenManager.clearTokens();
  }, []);

  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.changePassword({
        oldPassword,
        newPassword,
      });

      if (response.success) {
        return { success: true };
      } else {
        throw new Error(response.message || 'Password change failed');
      }
    } catch (err) {
      const errorMessage = err.message || 'Password change failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!token,
    login,
    register,
    logout,
    changePassword,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
